import { AssetLinkingInput, GetAssignmentAssetsQuery } from "../../../graphql/generated";

/**
 * Represents a conflict when an asset is assigned to multiple drivers in overlapping time periods
 */
export type AssetConflict = {
  assetType: 'tractor' | 'trailer' | 'additionalTrailer';
  assetId: string;
  assetName: string;
  conflictingDriverId: string;
  conflictingDriverName: string;
  conflictingPeriod: { start: Date | null; end: Date | null };
};

/**
 * Represents a pending asset change that hasn't been saved yet
 */
export type PendingAssetChange = {
  driverId: string;
  assetLinkingInput: AssetLinkingInput;
  assetLinkingId?: string;
};

// Use existing types from GraphQL queries
type ExistingAssetLinking = GetAssignmentAssetsQuery["assetLinkings"]["data"][0];
type DriverInfo = GetAssignmentAssetsQuery["drivers"]["data"][0];

/**
 * Helper function to check if two time periods overlap
 */
function periodsOverlap(
  start1: number | null,
  end1: number | null,
  start2: number | null,
  end2: number | null
): boolean {
  // If either period has no dates, consider them as overlapping (always active)
  if (start1 === null && end1 === null) return true;
  if (start2 === null && end2 === null) return true;
  
  // If one period has no end, it extends indefinitely
  const effectiveEnd1 = end1 ?? Infinity;
  const effectiveEnd2 = end2 ?? Infinity;
  const effectiveStart1 = start1 ?? -Infinity;
  const effectiveStart2 = start2 ?? -Infinity;
  
  // Check for overlap: periods overlap if one starts before the other ends
  return effectiveStart1 < effectiveEnd2 && effectiveStart2 < effectiveEnd1;
}

/**
 * Helper to get driver name from driver info
 */
function getDriverName(driver: DriverInfo): string {
  return `${driver.firstname} ${driver.lastname}`;
}

/**
 * Helper to check if an asset conflicts with existing database assignments
 */
function checkExistingAssetConflicts(
  assetId: string,
  assetType: 'tractor' | 'trailer' | 'additionalTrailer',
  currentDriverId: string,
  newStart: number | null,
  newEnd: number | null,
  existingAssetLinkings: ExistingAssetLinking[],
  drivers: DriverInfo[]
): AssetConflict[] {
  const conflicts: AssetConflict[] = [];

  for (const linking of existingAssetLinkings) {
    if (!linking.driver || linking.driver._id === currentDriverId) {
      continue;
    }

    // Check if this linking contains the asset we're looking for
    let assetMatch = false;
    let assetName = assetId;

    if (assetType === 'tractor' && linking.tractor?._id === assetId) {
      assetMatch = true;
      assetName = linking.tractor.serialNumber || linking.tractor._id;
    } else if (linking.trailer?._id === assetId) {
      assetMatch = true;
      assetName = linking.trailer.serialNumber || linking.trailer._id;
    } else if (linking.additionalTrailers?.some(t => t._id === assetId)) {
      assetMatch = true;
      const trailer = linking.additionalTrailers.find(t => t._id === assetId);
      assetName = trailer?.serialNumber || assetId;
    }

    if (!assetMatch) {
      continue;
    }

    // Check for time period overlap
    const existingStart = linking.startDate 
      ? new Date(linking.startDate).getTime() 
      : null;
    const existingEnd = linking.endDate 
      ? new Date(linking.endDate).getTime() 
      : null;
    
    if (periodsOverlap(newStart, newEnd, existingStart, existingEnd)) {
      const driver = drivers.find(d => d._id === linking.driver?._id);
      conflicts.push({
        assetType,
        assetId,
        assetName,
        conflictingDriverId: linking.driver._id,
        conflictingDriverName: driver ? getDriverName(driver) : linking.driver._id,
        conflictingPeriod: { 
          start: linking.startDate ? new Date(linking.startDate) : null, 
          end: linking.endDate ? new Date(linking.endDate) : null 
        },
      });
    }
  }

  return conflicts;
}

/**
 * Helper to check if an asset conflicts with pending changes
 */
function checkPendingAssetConflicts(
  assetId: string,
  assetType: 'tractor' | 'trailer' | 'additionalTrailer',
  currentDriverId: string,
  newStart: number | null,
  newEnd: number | null,
  pendingAssetChanges: Map<string, PendingAssetChange>,
  drivers: DriverInfo[]
): AssetConflict[] {
  const conflicts: AssetConflict[] = [];

  for (const [pendingDriverId, pendingChange] of Array.from(pendingAssetChanges.entries())) {
    if (pendingDriverId === currentDriverId) {
      continue;
    }

    // Check if this pending change contains the asset we're looking for
    let assetMatch = false;

    if (assetType === 'tractor' && pendingChange.assetLinkingInput.tractor === assetId) {
      assetMatch = true;
    } else if (pendingChange.assetLinkingInput.trailer === assetId) {
      assetMatch = true;
    } else if (pendingChange.assetLinkingInput.additionalTrailers?.includes(assetId)) {
      assetMatch = true;
    }

    if (!assetMatch) {
      continue;
    }

    // Check for time period overlap
    const pendingStart = pendingChange.assetLinkingInput.startDate 
      ? new Date(pendingChange.assetLinkingInput.startDate).getTime() 
      : null;
    const pendingEnd = pendingChange.assetLinkingInput.endDate 
      ? new Date(pendingChange.assetLinkingInput.endDate).getTime() 
      : null;
    
    if (periodsOverlap(newStart, newEnd, pendingStart, pendingEnd)) {
      const driver = drivers.find(d => d._id === pendingDriverId);
      conflicts.push({
        assetType,
        assetId,
        assetName: assetId, // For pending changes, we only have the ID
        conflictingDriverId: pendingDriverId,
        conflictingDriverName: driver ? getDriverName(driver) : pendingDriverId,
        conflictingPeriod: { 
          start: pendingChange.assetLinkingInput.startDate ? new Date(pendingChange.assetLinkingInput.startDate) : null, 
          end: pendingChange.assetLinkingInput.endDate ? new Date(pendingChange.assetLinkingInput.endDate) : null 
        },
      });
    }
  }

  return conflicts;
}

/**
 * Validates that an asset assignment doesn't conflict with existing or pending assignments
 * 
 * @param assetLinkingInput - The new asset linking to validate
 * @param currentDriverId - The driver receiving the asset assignment
 * @param existingAssetLinkings - All existing asset linkings from the database
 * @param pendingAssetChanges - Map of pending asset changes not yet saved
 * @param drivers - List of all drivers for name lookup
 * @returns Array of conflicts found (empty if no conflicts)
 */
export function validateAssetAssignment(
  assetLinkingInput: AssetLinkingInput,
  currentDriverId: string,
  existingAssetLinkings: ExistingAssetLinking[],
  pendingAssetChanges: Map<string, PendingAssetChange>,
  drivers: DriverInfo[]
): AssetConflict[] {
  const conflicts: AssetConflict[] = [];
  
  const newStart = assetLinkingInput.startDate 
    ? new Date(assetLinkingInput.startDate).getTime() 
    : null;
  const newEnd = assetLinkingInput.endDate 
    ? new Date(assetLinkingInput.endDate).getTime() 
    : null;

  // Check tractor conflicts
  if (assetLinkingInput.tractor) {
    conflicts.push(
      ...checkExistingAssetConflicts(
        assetLinkingInput.tractor,
        'tractor',
        currentDriverId,
        newStart,
        newEnd,
        existingAssetLinkings,
        drivers
      )
    );
    
    conflicts.push(
      ...checkPendingAssetConflicts(
        assetLinkingInput.tractor,
        'tractor',
        currentDriverId,
        newStart,
        newEnd,
        pendingAssetChanges,
        drivers
      )
    );
  }

  // Check trailer conflicts
  if (assetLinkingInput.trailer) {
    conflicts.push(
      ...checkExistingAssetConflicts(
        assetLinkingInput.trailer,
        'trailer',
        currentDriverId,
        newStart,
        newEnd,
        existingAssetLinkings,
        drivers
      )
    );
    
    conflicts.push(
      ...checkPendingAssetConflicts(
        assetLinkingInput.trailer,
        'trailer',
        currentDriverId,
        newStart,
        newEnd,
        pendingAssetChanges,
        drivers
      )
    );
  }

  // Check additional trailer conflicts
  if (assetLinkingInput.additionalTrailers) {
    for (const additionalTrailerId of assetLinkingInput.additionalTrailers) {
      conflicts.push(
        ...checkExistingAssetConflicts(
          additionalTrailerId,
          'additionalTrailer',
          currentDriverId,
          newStart,
          newEnd,
          existingAssetLinkings,
          drivers
        )
      );
      
      conflicts.push(
        ...checkPendingAssetConflicts(
          additionalTrailerId,
          'additionalTrailer',
          currentDriverId,
          newStart,
          newEnd,
          pendingAssetChanges,
          drivers
        )
      );
    }
  }

  return conflicts;
}
